import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Checkbox,
    Divider,
    ListItemText,
    Menu,
    MenuItem,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { getTenantDisplayName } from '../../common/tenantDisplayNames';
import { MasterListState } from './useMasterListState';

interface TenantFilterMenuProps {
    /** The `useMasterListState` result — supplies the tenant list, the
     * current selection and the All / None / Customer service actions. */
    controls: MasterListState;
}

/**
 * Shared tenant filter dropdown for the all-tenant (Master Mode) list
 * pages. Offers the All / None / Customer service presets plus a
 * per-tenant checkbox list. "Customer service" selects the tenants where
 * kolula covers (end-)customer service (see `customerServiceTenants.ts`).
 */
const TenantFilterMenu: React.FC<TenantFilterMenuProps> = ({ controls }) => {
    const { t } = useTranslation();
    const {
        accessibleTenants,
        selectedTenantSlugs,
        toggleTenantSelected: onToggleTenant,
        selectAllTenants: onSelectAll,
        clearAllTenants: onClearAll,
        selectCustomerServiceTenants: onSelectCustomerService,
    } = controls;
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const isOpen = Boolean(anchorEl);
    const openMenu = (event: React.MouseEvent<HTMLButtonElement>) =>
        setAnchorEl(event.currentTarget);
    const closeMenu = () => setAnchorEl(null);

    if (accessibleTenants.length === 0) return null;

    // No own margin — the scaffold's filter/reload row handles spacing.
    return (
        <div>
            <Button
                variant={'outlined'}
                size={'small'}
                onClick={openMenu}
                endIcon={<KeyboardArrowDownIcon />}
                sx={{
                    color: 'rgb(var(--color-foreground))',
                    borderColor: 'rgba(0,0,0,0.15)',
                    backgroundColor: 'rgb(var(--color-text-contrast))',
                    '&:hover': {
                        borderColor: 'rgba(0,0,0,0.3)',
                        backgroundColor: 'rgb(var(--color-text-contrast))',
                    },
                }}
            >
                {t('master.tenant_filter_label', {
                    selected: selectedTenantSlugs.size,
                    total: accessibleTenants.length,
                })}
            </Button>
            <Menu
                anchorEl={anchorEl}
                open={isOpen}
                onClose={closeMenu}
                slotProps={{ paper: { sx: { maxHeight: 480, minWidth: 240 } } }}
            >
                <MenuItem onClick={onSelectAll} dense={true}>
                    <ListItemText
                        primary={t('master.chips.all')}
                        primaryTypographyProps={{ fontSize: '0.875rem' }}
                    />
                </MenuItem>
                <MenuItem onClick={onClearAll} dense={true}>
                    <ListItemText
                        primary={t('master.chips.none')}
                        primaryTypographyProps={{ fontSize: '0.875rem' }}
                    />
                </MenuItem>
                <MenuItem onClick={onSelectCustomerService} dense={true}>
                    <ListItemText
                        primary={t('master.chips.customer_service')}
                        primaryTypographyProps={{ fontSize: '0.875rem' }}
                    />
                </MenuItem>
                <Divider />
                {accessibleTenants.map((slug) => (
                    <MenuItem
                        key={slug}
                        onClick={() => onToggleTenant(slug)}
                        dense={true}
                    >
                        <Checkbox
                            checked={selectedTenantSlugs.has(slug)}
                            size={'small'}
                            sx={{ p: 0, mr: 1 }}
                        />
                        <ListItemText primary={getTenantDisplayName(slug)} />
                    </MenuItem>
                ))}
            </Menu>
        </div>
    );
};

export default TenantFilterMenu;
